import pptxgen from 'pptxgenjs';
import { sanitizeHeaderText, downloadFilenameGeneratorForPPT } from '.';
import { IContent, IRelevantFiles, RESPONSE_TYPE } from '../../../models/IQueryType';
import { publicSources } from '../../../label.constant';
import LABELS from '../../../ui-constants/en.json';
import {
  decodeHTML,
  getResponseSourceType,
  getSourceType,
  toPascalCase
} from '../../../utils/commonUtils';
import { convertNumber } from './numberConverter';
export enum BulletPointType {
  hyphen = '-',
  dot = '•',
  number = 'number',
  NA = 'NA'
}
type ITableContentProps = {
  Content: IContent[];
  ResponseType: RESPONSE_TYPE;
  checkedCheckboxes?: any;
  eyIPInterRadio?: any;
  validURLinputValue?: any;
};

// Content length management constants
const MAX_CHARS_PER_SLIDE = 4000;
const MAX_CHARS_PER_CELL = 2000;
const MAX_ROWS_PER_SLIDE = 12;
const MAX_TEXT_LENGTH_PER_ROW = 200;
const MAX_TABLE_COLS = 12;

export const exportToCombinedPpt = (
  source: string,
  userQueryWithResponse?: any,
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  let pres = new pptxgen();
  let rows: any[] = [];
  const separatorOnlyRegex = /^\|(?:-+\|)+$/;
  const tableLineRegex = /^\|(?:\s*[^|\n]+\s*\|)+\s*$/;

  const checkContentLength = (
    text: string,
    maxLength: number = MAX_TEXT_LENGTH_PER_ROW
  ): string[] => {
    if (text.length <= maxLength) {
      return [text];
    }

    const chunks: string[] = [];
    let currentChunk = '';
    const words = text.split(' ');

    for (const word of words) {
      if ((currentChunk + ' ' + word).length > maxLength) {
        if (currentChunk) {
          chunks.push(currentChunk.trim());
          currentChunk = word;
        } else {
          // Handle case where single word exceeds max length
          chunks.push(word.substring(0, maxLength - 3) + '...');
          currentChunk = word.substring(maxLength - 3);
        }
      } else {
        currentChunk += (currentChunk ? ' ' : '') + word;
      }
    }

    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  };

  // Helper function to estimate slide content length
  const estimateSlideContentLength = (slideRows: pptxgen.TableRow[]): number => {
    return slideRows.reduce((total, row) => {
      return (
        total +
        row.reduce((rowTotal, cell) => {
          if (typeof cell === 'object' && 'text' in cell) {
            if (Array.isArray(cell.text)) {
              return (
                rowTotal +
                cell.text.reduce((textTotal, textProp) => {
                  return textTotal + (textProp.text?.length || 0);
                }, 0)
              );
            }
            return rowTotal + (cell.text?.length || 0);
          }
          return rowTotal + (typeof cell === 'string' && cell ? cell : 0);
        }, 0)
      );
    }, 0);
  };

  // Utility function for parsing markdown links and plain text
  const parseMarkdownLinks = (text: string, isBold: boolean = false): pptxgen.TextProps[] => {
    const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let parts: pptxgen.TextProps[] = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      // Add preceding plain text
      if (match.index > lastIndex) {
        const plainText = text.substring(lastIndex, match.index);
        if (plainText) {
          parts.push({
            text: plainText,
            options: { fontSize: 10, color: '000000', bold: isBold }
          });
        }
      }
      // Validate and format URL properly
      let url = match[2].trim();
      const linkText = match[1].trim();
      if (url && linkText) {
        url = url.replace(/(https|http)\/\//g, '$1://');
        // Additional URL validation
        try {
          const urlObj = new URL(url);
          // Ensure the protocol is http or https
          if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
            throw new Error('Invalid protocol');
          }
          // Create hyperlink with validated URL
          parts.push({
            text: linkText,
            options: {
              fontSize: 10,
              color: '0563c1',
              hyperlink: { url: url }
              // underline: true
            }
          });
        } catch (error) {
          // If URL is invalid, treat as plain text
          console.warn(`Invalid URL found: ${url}`, error);
          parts.push({
            text: `[${linkText}](${match[2]})`,
            options: { fontSize: 10, color: '000000', bold: isBold }
          });
        }
      } else {
        // If no valid URL or text, treat as plain text
        parts.push({
          text: match[0],
          options: { fontSize: 10, color: '000000', bold: isBold }
        });
      }
      lastIndex = match.index + match[0].length;
    }
    // Add any remaining plain text after last link
    if (lastIndex < text.length) {
      const plainText = text.substring(lastIndex);
      if (plainText) {
        parts.push({
          text: plainText,
          options: { fontSize: 10, color: '000000', bold: isBold }
        });
      }
    }
    // If no links found, return the original text
    if (parts.length === 0) {
      parts.push({
        text: text,
        options: { fontSize: 10, color: '000000', bold: isBold }
      });
    }
    return parts;
  };

  //Parsing markdown cells
  const parseMarkdownCell = (text: string): pptxgen.TextProps | pptxgen.TextProps[] => {
    if (!text || typeof text !== 'string') {
      return {
        text: String(text || ''),
        options: { fontSize: 10, color: '000000' }
      };
    }
    // Handle bold text
    const boldMatch = text.match(/^\*\*(.*?)\*\*$/);
    if (boldMatch) {
      return {
        text: boldMatch[1],
        options: { bold: true, fontSize: 10, color: '000000' }
      };
    }
    // Handle markdown links
    const parts = parseMarkdownLinks(text);
    if (parts.length === 1 && !parts[0].options?.hyperlink) {
      return parts[0];
    } else if (parts.length > 1 || parts[0].options?.hyperlink) {
      return parts;
    }
    // Return plain text
    return {
      text,
      options: { fontSize: 10, color: '000000' }
    };
  };

  const { user, SEResponse } = userQueryWithResponse;

  const handleCsvFormt = (data: string) => {
    try {
      return data
        .replaceAll('---|', '')
        .replaceAll('--', '')
        .replaceAll(/\s{3,}/g, '')
        .replaceAll('|', ',')
        .replaceAll(',\n,', ',\n')
        .replaceAll(',\n-', '\n')
        .replaceAll('\n\n', '\n')
        .replaceAll(', Fiscal Year', 'Fiscal Year')
        .replaceAll('sqlQuery', '')
        .trim();
    } catch (err) {
      return '';
    }
  };

  // Enhanced table generation with content length checking
  const handleTableGeneration = (data: ITableContentProps) => {
    if (
      data?.Content &&
      data.Content.length > 0 &&
      data.ResponseType === RESPONSE_TYPE.TABLE &&
      data.Content.every((el) => Boolean(el) && typeof el === 'object')
    ) {
      const tableRows: pptxgen.TableRow[] = [];
      const headers = Object.keys(data.Content[0]);

      // Check if table has too many columns
      if (headers.length > MAX_TABLE_COLS) {
        // Split table into multiple slides
        const columnsPerSlide = MAX_TABLE_COLS;
        const numberOfSlides = Math.ceil(headers.length / columnsPerSlide);

        for (let slideIndex = 0; slideIndex < numberOfSlides; slideIndex++) {
          const startCol = slideIndex * columnsPerSlide;
          const endCol = Math.min(startCol + columnsPerSlide, headers.length);
          const slideHeaders = headers.slice(startCol, endCol);

          const slideTableRows: pptxgen.TableRow[] = [];

          // Add headers for this slide
          slideTableRows.push(
            slideHeaders.map((el) => ({
              text: toPascalCase(sanitizeHeaderText(el)),
              options: { bold: true }
            }))
          );

          // Add data rows for this slide
          data.Content.forEach((el: IContent) => {
            const rowValues: pptxgen.TableCell[] = [];
            slideHeaders.forEach((key) => {
              const value = el[key];
              let cellText = '';

              if (
                key.toLowerCase().includes('year') ||
                key.toLowerCase().includes('id') ||
                key.toLowerCase().includes('quarter') ||
                key.toLowerCase().includes('count') ||
                key.toLowerCase().includes('date') ||
                key.toLowerCase().includes('Code') ||
                key.toLocaleLowerCase().includes('portfolio')
              ) {
                cellText = String(value);
              } else {
                cellText = convertNumber(value, 1).val;
              }

              // Truncate cell text if too long
              if (cellText.length > MAX_CHARS_PER_CELL) {
                cellText = cellText.substring(0, MAX_CHARS_PER_CELL) + '...';
              }

              rowValues.push({ text: cellText });
            });
            slideTableRows.push(rowValues);
          });

          // Add slide with appropriate title
          const slideTitle =
            numberOfSlides > 1 ? `Table (Part ${slideIndex + 1}/${numberOfSlides})` : 'Table';
          pres
            .addSlide()
            .addText(slideTitle, { x: 0.5, y: 0.5, fontSize: 16, bold: true })
            .addTable(slideTableRows, {
              x: 0.5,
              y: 1,
              autoPage: true,
              color: '000000',
              fontSize: 9,
              border: { color: 'e8e8ee', pt: 1, type: 'solid' },
              fill: { color: 'f7f7f8' },
              colW: 1.5
            });
        }
      } else {
        // Original table generation for tables with acceptable column count
        tableRows.push(
          headers.map((el) => ({
            text: toPascalCase(sanitizeHeaderText(el)),
            options: { bold: true }
          }))
        );

        data.Content.forEach((el: IContent) => {
          const rowValues: pptxgen.TableCell[] = [];
          Object.entries(el).forEach(([key, value]) => {
            let cellText = '';

            if (
              key.toLowerCase().includes('year') ||
              key.toLowerCase().includes('id') ||
              key.toLowerCase().includes('quarter') ||
              key.toLowerCase().includes('count') ||
              key.toLowerCase().includes('date') ||
              key.toLowerCase().includes('Code') ||
              key.toLocaleLowerCase().includes('portfolio')
            ) {
              cellText = String(value);
            } else {
              cellText = convertNumber(value, 1).val;
            }

            // Truncate cell text if too long
            if (cellText.length > MAX_CHARS_PER_CELL) {
              cellText = cellText.substring(0, MAX_CHARS_PER_CELL) + '...';
            }

            rowValues.push({ text: cellText });
          });
          tableRows.push(rowValues);
        });

        pres.addSlide().addTable(tableRows, {
          autoPage: true,
          color: '000000',
          fontSize: 9,
          border: { color: 'e8e8ee', pt: 1, type: 'solid' },
          fill: { color: 'f7f7f8' },
          colW: tableRows[0].length > 8 ? 1.2 : undefined
        });
      }
    }
  };

  const countSpacesBeforeHyphen = (input: string, bulletPointType: BulletPointType): number => {
    let indexOfHyphen = 0;
    if (bulletPointType === BulletPointType.hyphen) {
      // indexOfHyphen = input.indexOf('-');
      const match = input.match(/^(\s*)-/);
      return match ? match[1].length : 0;
    }
    if (bulletPointType === BulletPointType.dot) {
      indexOfHyphen = input.indexOf('•');
    }

    if (bulletPointType === BulletPointType.number) {
      const match = input.match(/^(\s*)/); // Matches leading whitespace characters
      return match ? match[1].length : 0;
    }
    const substringBeforeHyphen = input.slice(0, indexOfHyphen);
    const spaceCount = (substringBeforeHyphen.match(/ /g) || []).length;
    return spaceCount;
  };

  const isNumericBullet = (sentence: string): boolean => {
    const numericBulletPattern = /^\s*\d+[\.\)\-]\s+/;
    return numericBulletPattern.test(sentence);
  };

  const handleRowCreation = (
    data: string,
    hasBulletpoint: boolean,
    isBold: boolean,
    indentLevel: number,
    isExtraSpace: boolean
  ): pptxgen.TextProps[] => {
    let dynamicTextArray: pptxgen.TextProps[] = [];
    if (hasBulletpoint) {
      dynamicTextArray.push({
        text: '.',
        options: {
          fontSize: 10,
          bold: isBold,
          indentLevel: indentLevel,
          bullet: true,
          align: 'left',
          color: 'FFFFFF'
        }
      });
      dynamicTextArray.push(...parseMarkdownLinks(data, isBold));
    } else {
      if (!isExtraSpace) {
        dynamicTextArray.push({
          text: '.',
          options: {
            fontSize: 10,
            bold: isBold,
            italic: false,
            color: 'FFFFFF'
          }
        });
      }
      dynamicTextArray.push(...parseMarkdownLinks(data, isBold));
    }
    return dynamicTextArray;
  };

  try {
    const userQuery = user && decodeHTML(user?.summary);
    const SEChatSummary = SEResponse && handleCsvFormt(SEResponse?.summary);

    const rawSESummary = SEResponse?.summary.replaceAll('sqlQuery', '');
    // .replace(/-/g, '•').replace(/\*\*/g, '');
    // const sourceSection = SEResponse && handleSourceSection();
    const sourceMapping = () => {
      if (!Array.isArray(checkedCheckboxes) || checkedCheckboxes.length === 0) return '';
      const [embryonic, openAI, marketReports] = publicSources;
      const hasEmbryonic = checkedCheckboxes.includes(embryonic);
      const hasOpenAI = checkedCheckboxes.includes(openAI);
      const hasMarket = checkedCheckboxes.includes(marketReports);

      if (checkedCheckboxes.length === 1) {
        if (hasMarket) return 'Market reports';
        if (hasEmbryonic) return 'Embryonic';
        if (hasOpenAI) return 'Open AI';
      }
      if (checkedCheckboxes.length === 2) {
        if (hasMarket && hasEmbryonic) return 'WithEmbMR';
        if (hasMarket && hasOpenAI) return 'WithOAIMR';
        if (hasEmbryonic && hasOpenAI) return 'Combined';
      }
      if (checkedCheckboxes.length === 3 && hasMarket && hasEmbryonic && hasOpenAI) {
        return 'WithMR';
      }
      return '';
    };

    const headerPattern1 = /^#\s+(.+)$/gm;
    const headerPattern2 = /^##\s+(.+)$/gm;
    const headerPattern3 = /^###\s+(.+)$/gm;
    const headerPattern4 = /^####\s+(.+)$/gm;
    const headerPattern5 = /^#####\s+(.+)$/gm;
    const headerPattern6 = /^\s*####\s+(.+)$/gm;
    const { relevantFiles } = SEResponse;
    const sourceTable = [
      {
        text: `Source: ${getResponseSourceType(sourceMapping())}`,
        options: {
          border: { type: 'none' },
          colspan: !relevantFiles ? 0 : 3,
          margin: !relevantFiles ? [10, 0, 0, 10] : [10, 0, 10, 0],
          fill: { color: '#ffffff' }
        }
      }
    ];
    const splitlines = rawSESummary?.split('\n') || [];
    for (let i = 0; i < splitlines.length; i++) {
      let line = splitlines[i];
      if (tableLineRegex.test(line)) {
        const blockLines = [line];
        let j = i + 1;
        while (j < splitlines.length && tableLineRegex.test(splitlines[j].trim())) {
          if (!separatorOnlyRegex.test(splitlines[j])) {
            blockLines.push(splitlines[j].trim());
          }
          j++;
        }
        const pptRows: pptxgen.TableRow[] = [];
        const headers = blockLines[0]
          .split('|')
          .map((h: any) => h.trim())
          .filter(Boolean);

        // Check if table headers are too long
        const truncatedHeaders = headers.map((header: string) =>
          header.length > 30 ? header.substring(0, 27) + '...' : header
        );

        pptRows.push(
          headers.map((text: any) => ({
            text,
            options: { bold: true, fontSize: 10, color: '000000', fill: 'F7F7F8' }
          }))
        );

        for (let k = 1; k < blockLines.length; k++) {
          const cells = blockLines[k]
            .split('|')
            .map((c: any) => c.trim())
            .filter(Boolean);

          // Truncate long cell content
          const truncatedCells = cells.map((cell: string) =>
            cell.length > MAX_CHARS_PER_CELL ? cell.substring(0, MAX_CHARS_PER_CELL) + '...' : cell
          );
          const cellRow: pptxgen.TableCell[] = [];
          for (const cellText of cells) {
            const parsedCell = parseMarkdownCell(cellText);
            if (Array.isArray(parsedCell)) {
              // For rich text with hyperlinks, create proper structure
              cellRow.push({
                text: parsedCell,
                options: { fill: { color: 'F7F7F8' } }
              });
            } else {
              // For simple text
              cellRow.push({
                text: parsedCell.text,
                options: {
                  ...parsedCell.options,
                  fill: { color: 'F7F7F8' }
                }
              });
            }
          }
          pptRows.push(cellRow);
        }
        rows.push([{ type: 'table', tableData: pptRows }]);
        i = j - 1;
        continue;
      }

      // Handle headers
      if (headerPattern1.test(line)) {
        line = line.replace('#', '');
        rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
      }
      // Check and apply header pattern 2
      else if (headerPattern2.test(line)) {
        line = line.replace('##', '');
        rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
      }
      // Check and apply header pattern 3
      else if (headerPattern3.test(line)) {
        line = line.replace('###', '');
        rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
      }
      // Check and apply header pattern 4
      else if (headerPattern4.test(line)) {
        line = line.replace('####', '');
        rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
      }
      // Check and apply header pattern 5
      else if (headerPattern5.test(line)) {
        line = line.replace('#####', '');
        rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
      }
      // Check and apply header pattern 6
      else if (headerPattern6.test(line)) {
        line = line.replace('####', '');
        rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
      } else if (line === '---') {
        line = line.replace('---', '');
        rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
      } else {
        if (line.trim().length > 0) {
          let currentIndentLevel = 0;
          let isNumericbulletpoint = isNumericBullet(line);
          let startsWithHyphen = line.trim().startsWith('-');

          if (isNumericbulletpoint) {
            currentIndentLevel = countSpacesBeforeHyphen(line, BulletPointType.number);
          } else {
            currentIndentLevel = countSpacesBeforeHyphen(line, BulletPointType.hyphen);
          }

          currentIndentLevel = currentIndentLevel > 0 ? currentIndentLevel - 1 : 0;

          line = line.replace(/^\s*-/, '• ');
          const updatedText = line.replace(/\*\*(.*?)\*\*/g, '**[Bold]$1[/Bold]**');
          const splitText = updatedText.split(/\*\*/);

          for (let i = splitText.length - 1; i >= 0; i--) {
            if (splitText[i].trim() === '•') {
              splitText.splice(i, 1);
            }
          }

          let dynamicTextArray: pptxgen.TextProps[] = [];
          for (let j = 0; j < splitText.length; j++) {
            const isBold = splitText[j].includes('[Bold]');

            if (isNumericBullet(splitText[1])) {
              splitText[j + 1].replace('[Bold]', '[Bold]' + splitText[1]);
            } else {
              if (startsWithHyphen && isBold) {
                splitText[j] = '• ' + splitText[j];
              }

              if (splitText[j].trim().length > 0) {
                // Check if text contains [Bold]
                let cleanedText = splitText[j].replace('[Bold]', '').replace('[/Bold]', ' ');
                let isExtraSpace = false;
                if (cleanedText == updatedText) {
                  isExtraSpace = true;
                }

                let textprops = handleRowCreation(
                  cleanedText,
                  isNumericbulletpoint ? isNumericbulletpoint : startsWithHyphen,
                  isBold,
                  currentIndentLevel,
                  isExtraSpace
                );
                // Check if textprops is an array and handle accordingly
                if (Array.isArray(textprops)) {
                  dynamicTextArray.push(...textprops);
                } else {
                  dynamicTextArray.push(textprops);
                }
              }
              startsWithHyphen = false;
              isNumericbulletpoint = false;
            }
          }
          rows.push([{ text: dynamicTextArray }]);
        }
      }
    }

    const SESummaryRow: any = [
      [{ text: `${userQuery}`, options: { bold: true } }],
      [
        {
          text: `${SEChatSummary}`,
          options:
            SEResponse?.responseType === 'stop_generation' ? { color: '656579', italic: true } : {}
        }
      ]
    ];

    if (SEResponse?.isParitallyAbortedResponse) {
      SESummaryRow.push([
        {
          text: LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG,
          options: { color: '656579', italic: true }
        }
      ]);
    }

    if (userQuery) {
      rows.unshift([
        { text: `${userQuery}`, options: { bold: true, fontSize: 12, color: '000000' } }
      ]);
      rows.unshift([{ text: '', options: {} }]); // Add an empty row for spacing
    }

    let slideRows: pptxgen.TableRow[] = [];
    let currentSlideLength = 0;

    const flushTextSlide = () => {
      if (slideRows.length > 0) {
        const dynamicHeight = Math.min((slideRows.length / MAX_ROWS_PER_SLIDE) * 8.5, 9);
        pres.addSlide().addTable(slideRows, {
          autoPage: false,
          h: dynamicHeight,
          rowH: Math.max(0.1, 0.8 / slideRows.length), // Adjust row height based on content
          fontSize: slideRows.length > 15 ? 9 : 10 // Smaller font for dense content
        });
        slideRows = [];
        currentSlideLength = 0;
      }
    };

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (row[0]?.type === 'table') {
        flushTextSlide();
        pres.addSlide().addTable(row[0].tableData, {
          autoPage: true,
          border: { type: 'solid', pt: 1, color: 'E8E8EE' },
          fill: { color: 'F7F7F8' },
          fontSize: 10,
          colW: row[0].tableData[0].map(() => Math.min(1.5, 10 / row[0].tableData[0].length))
        });
      } else {
        const rowLength = estimateSlideContentLength([row]);

        // Check if adding this row would exceed limits
        if (
          slideRows.length >= MAX_ROWS_PER_SLIDE ||
          (currentSlideLength + rowLength > MAX_CHARS_PER_SLIDE && slideRows.length > 0)
        ) {
          flushTextSlide();
        }
        slideRows.push(row);
        currentSlideLength += rowLength;
      }
    }

    // Final flush if anything left
    flushTextSlide();
    handleTableGeneration(SEResponse);

    if (!relevantFiles) {
      if (SEChatSummary?.isParitallyAbortedResponse) {
        rows.push([
          {
            text: LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG,
            options: { color: '656579', italic: true }
          }
        ]);
      }
      rows.push(sourceTable as pptxgen.TableRow);
    }

    if (
      relevantFiles &&
      relevantFiles.length > 0 &&
      relevantFiles.every((el: any) => Boolean(el) && typeof el === 'object')
    ) {
      const documentTableRows: pptxgen.TableRow[] = [];
      const docuementTableHead: pptxgen.TableCell[] = [
        {
          text: sanitizeHeaderText('Document Title'),
          options: { bold: true }
        },
        {
          text: sanitizeHeaderText('Page nos.'),
          options: { bold: true }
        },
        {
          text: sanitizeHeaderText('Source'),
          options: { bold: true }
        }
      ];
      documentTableRows.push(docuementTableHead);
      relevantFiles.forEach((el: Partial<IRelevantFiles>) => {
        const rowValues: pptxgen.TableCell[] = [];
        const filesName = el.file_name;
        let filesPage = '';

        el.page_number?.forEach((slide: string, i) => {
          if (el?.page_number && el.page_number.length > i + 1) {
            filesPage = filesPage + slide + ', ';
          } else {
            filesPage = filesPage + slide;
          }
        });

        rowValues.push({
          text: filesName
        });
        rowValues.push({
          text: filesPage
        });
        rowValues.push({
          text: el.source || el.sourceType
        });
        documentTableRows.push(rowValues);
      });

      if (SEChatSummary?.isParitallyAbortedResponse) {
        documentTableRows.push([
          {
            text: LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG,
            options: { color: '656579', italic: true }
          }
        ]);
      }

      documentTableRows.push(sourceTable as pptxgen.TableRow);

      pres.addSlide().addTable(documentTableRows, {
        border: { color: 'e8e8ee', pt: 1, type: 'solid' },
        fill: {
          color: 'f7f7f8'
        },
        autoPage: true,
        autoPageCharWeight: -0.5,
        autoPageLineWeight: 0.5
      });
    }

    pres
      .write({ outputType: 'blob' })
      .then((file) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(file as Blob);
        link.download = downloadFilenameGeneratorForPPT(
          checkedCheckboxes,
          eyIPInterRadio,
          validURLinputValue,
          'ppt'
        );
        link.click();
      })
      .catch((err) => {});
  } catch (err) {
    console.log('::::PPT error', err);
  }
};
